import { type Theme } from "@/theme/theme-context";
import { TradingViewAdvancedChart } from "./tradingview-advanced-chart";
import { useMemo, useState, useEffect } from "react";


// When dragging a new panel in, the pointer events on the iframe mess up the drag events.
// So when we are not hovering over the chart, we set the pointer events to none so the drag takes priority.
const fixDragClassNames =
  "[&_iframe]:!pointer-events-none hover:[&_iframe]:!pointer-events-auto";

type MarketChartProps = {
  theme: Theme;
  productId: string;
  isMobile?: boolean;
};

const usePrefersFinePointer = () => {
  const [hasFinePointer, setHasFinePointer] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    return window.matchMedia("(pointer: fine)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const query = window.matchMedia("(pointer: fine)");
    const listener = (event: MediaQueryListEvent) => {
      setHasFinePointer(event.matches);
    };

    if (query.addEventListener) {
      query.addEventListener("change", listener);
      return () => query.removeEventListener("change", listener);
    }

    query.addListener(listener);
    return () => query.removeListener(listener);
  }, []);

  return hasFinePointer;
};

export const MarketChart = ({ theme, productId, isMobile = false }: MarketChartProps) => {
  // TradingView now uses Nordex symbols (BTC-USD) directly
  // The backend TradingView datafeed API handles provider symbol mapping internally
  const tradingViewSymbol = productId;
  const hasFinePointer = usePrefersFinePointer();
  const containerClass = useMemo(() => {
    return `absolute -inset-px ${hasFinePointer ? fixDragClassNames : ""}`;
  }, [hasFinePointer]);

  return (
    // inset to remove border on iframe
    <div className={containerClass}>
      <TradingViewAdvancedChart
        productId={tradingViewSymbol}
        theme={theme}
        isMobile={isMobile}
      />
    </div>
  );
};
